import { useEffect } from "react";
import { BottomSheet } from "@/ui/components";
import { Button } from "@/ui/components/Button";
import { Icon } from "@/ui/icons/Icon";
import { trackingService } from "@/lib/tracking";
import { AddBankCardResultTypes } from "../../types/bankCard";
import styles from "./BankCardResultSheet.module.scss";

const SUCCESS_SHOWN_MS = 4000;

const FAILURE_TITLE = "Cardul nu a putut fi adăugat";

// The reasons the user can act on; any other rejection gets the general one.
const FAILURE_DESCRIPTIONS: Partial<Record<AddBankCardResultTypes, string>> = {
  [AddBankCardResultTypes.InsufficientFunds]:
    "Pentru a verifica validitatea cardului, este necesar să ai cel puțin 1 leu în cont.",
  [AddBankCardResultTypes.Restricted]:
    "Cererea a fost respinsă de banca emitentă. Este posibil ca banca ta să nu permită tranzacțiile online.",
  [AddBankCardResultTypes.UsedOnAnotherAccount]:
    "Acest card este deja folosit de alt utilizator.",
};
const DEFAULT_FAILURE_DESCRIPTION =
  "Verifică datele cardului sau încearcă alt card.";

// Still being checked by the partner: neither added nor rejected yet.
const isBankCardResultShown = (result: string | undefined) =>
  !!result && result !== AddBankCardResultTypes.Processing;

interface BankCardResultSheetProps {
  // The partner's result, from its return address; none hides the sheet.
  result: AddBankCardResultTypes | undefined;
  onClose: () => void;
  onRetry: () => void;
  isRetrying: boolean;
}

/**
 * What came of adding a card at the partner (Libra), over the screen that
 * asked for it: added (closes by itself), or rejected with a retry.
 */
export function BankCardResultSheet({
  result,
  onClose,
  onRetry,
  isRetrying,
}: BankCardResultSheetProps) {
  const isOpen = isBankCardResultShown(result);
  const isSuccess = result === AddBankCardResultTypes.Success;

  useEffect(() => {
    if (isOpen) {
      void trackingService.trackEvent("bank_card_result_shown", { result });
    }
  }, [isOpen, result]);

  useEffect(() => {
    if (!isOpen || !isSuccess) return;

    const timer = setTimeout(onClose, SUCCESS_SHOWN_MS);
    return () => clearTimeout(timer);
  }, [isOpen, isSuccess]);

  const title = isSuccess ? "Card adăugat cu succes" : FAILURE_TITLE;
  const description = isSuccess
    ? "Banii vor fi transferați pe acest card după semnarea contractului."
    : (result && FAILURE_DESCRIPTIONS[result]) || DEFAULT_FAILURE_DESCRIPTION;

  return (
    <BottomSheet
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      ariaLabel={title}
      ariaDescription={description}
      footer={
        isSuccess ? undefined : (
          <Button
            trackingId="bank_card_result_retry"
            variant="tonal"
            text="Încearcă din nou"
            loading={isRetrying}
            onClick={onRetry}
            className={styles.button}
          />
        )
      }
    >
      <div className={styles.content}>
        <Icon
          name={
            isSuccess ? "check-circle-outline" : "exclamation-circle-outline"
          }
          size={48}
          className={isSuccess ? styles.iconSuccess : styles.iconFailure}
        />
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.description}>{description}</p>
      </div>
    </BottomSheet>
  );
}
